import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useUser } from '../../../../../Context/UserContext';

export interface SupportChatAttachment {
  id: number;
  message_id: number;
  mime_type: string;
  byte_size: number;
  width: number | null;
  height: number | null;
  created: string;
  customer_url: string;
  staff_url?: string;
  url?: string;
}

export interface SupportChatMessage {
  id: number;
  thread_id: number;
  sender_type: 'customer' | 'staff';
  sender_user_id: number | null;
  body: string;
  created: string;
  attachments: SupportChatAttachment[];
}

export interface SupportChatThread {
  id: number;
  customer_id: number;
  status: string;
  created: string;
  last_message_at: string | null;
  staff_read_at: string | null;
  customer_read_at: string | null;
  archived_at: string | null;
  staff_unread: boolean;
  customer_unread: boolean;
  messages: SupportChatMessage[];
}

const POLL_INTERVAL_MS = 30_000;

const jsonHeaders = {
  Accept: 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
};

interface UseSupportChatThreadResult {
  thread: SupportChatThread | null;
  isLoading: boolean;
  sendError: string | null;
  isSending: boolean;
  sendMessage: (body: string, images: File[]) => Promise<boolean>;
  markAsRead: () => Promise<void>;
  refresh: () => Promise<void>;
}

export default function useSupportChatThread(isActive: boolean = true): UseSupportChatThreadResult {
  const { user } = useUser();
  const userId = user?.id;
  const [thread, setThread] = useState<SupportChatThread | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const cancelledRef = useRef(false);
  // Tracks the in-flight refresh request so a slow earlier response cannot
  // overwrite state with stale data after a faster later response landed.
  const abortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(async () => {
    if (!userId) {
      if (!cancelledRef.current) {
        setThread(null);
        setIsLoading(false);
      }
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const response = await axios.get<SupportChatThread | null>(
        '/api/onlineshop/support-chat/active-thread',
        { headers: jsonHeaders, signal: controller.signal }
      );
      if (cancelledRef.current || controller.signal.aborted) return;
      setThread(response.data ?? null);
    } catch (e) {
      if (axios.isCancel(e)) return;
      console.error('support-chat refresh:', e);
      // keep last known state — don't surface transient polling errors
    } finally {
      if (!cancelledRef.current && !controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [userId]);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
      abortRef.current?.abort();
    };
  }, []);

  // Initial fetch & re-fetch when the tab becomes active again.
  useEffect(() => {
    if (!isActive) return;
    refresh();
  }, [isActive, refresh]);

  // Poll only while the tab is active and the document is visible.
  useEffect(() => {
    if (!userId) return;
    if (!isActive) return;

    const interval = window.setInterval(() => {
      if (!document.hidden) {
        refresh();
      }
    }, POLL_INTERVAL_MS);
    const onVisibility = () => {
      if (!document.hidden) {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [userId, isActive, refresh]);

  const sendMessage = useCallback(
    async (body: string, images: File[]): Promise<boolean> => {
      if (!userId) return false;
      setIsSending(true);
      setSendError(null);
      try {
        const formData = new FormData();
        formData.append('body', body);
        images.forEach((file) => {
          formData.append('images[]', file);
        });
        await axios.post('/api/onlineshop/support-chat/messages', formData, {
          headers: jsonHeaders,
        });
        await refresh();
        return true;
      } catch (e: any) {
        const detail = e?.response?.data?.detail;
        setSendError(
          typeof detail === 'string' && detail.length > 0
            ? detail
            : 'Failed to send message. Please try again.'
        );
        return false;
      } finally {
        setIsSending(false);
      }
    },
    [userId, refresh]
  );

  const markAsRead = useCallback(async () => {
    if (!userId) return;
    // Failures are intentionally re-thrown so the caller can roll back its
    // dedup state (see Messages.tsx). Network errors are still logged for
    // visibility.
    try {
      await axios.post('/api/onlineshop/support-chat/mark-read', new FormData(), {
        headers: jsonHeaders,
      });
    } catch (e) {
      console.error('support-chat markAsRead:', e);
      throw e;
    }
    await refresh();
  }, [userId, refresh]);

  return { thread, isLoading, isSending, sendError, sendMessage, markAsRead, refresh };
}
